import type { HeaterType } from '../../engines/types';
import {
  getHeaterDepthM,
  getHeaterWidthM,
  type HeaterRef,
  heaterKindOf,
  INTERIOR_LINING_D_M,
  PANEL_T_M,
  STUD_D_M,
} from '../geometry/buildingDimensions';
import { chimneyBayShiftM } from '../chimney/calculateHeaterPlacement';

export const PARTITION_SAUNA_MIN_LENGTH_M = 1.5;
export const PARTITION_RELAX_MIN_LENGTH_M = 1.0;
export const PARTITION_SPLIT_STEP_M = 0.1;

/**
 * Minimum clearance (metres) between heater back edge and bench front edge.
 * Accounts for fire safety regulations and practical spacing.
 */
const HEATER_BENCH_CLEARANCE_M = 0.10;

/**
 * Wall build-out constants, read from domain/geometry/buildingDimensions, the single owner the
 * engines re-export as ENGINE_* (sD = stud depth, iL = interior lining — bastufolie + liggande
 * glespanel, pT = bastupanel thickness). They used to be local literals here
 * and had drifted: `iL` did not exist at all and `PT` was 0.015 against the
 * rule registry's 0.02, so every distance this module measured off a wall or
 * the divider was 28.5 mm short.
 */
const SD = STUD_D_M;
const IL = INTERIOR_LINING_D_M;
const PT = PANEL_T_M;
/** Stud face → finished interior face, the engines' `sD + iL + pT` chain. */
const WALL_BUILDOUT = SD + IL + PT;

/**
 * Divider-mounted heater geometry. The engine (twoRoomSauna.ts) and the BOM's
 * clearance layout (heaterClearanceLayout.ts) both place the stove through
 * `dividerHeaterCenterZM` below; these are its minimum gaps.
 */
const DIVIDER_FACE_OFFSET = SD / 2 + IL + PT / 2 + 0.001;
const DIVIDER_SHIELD_T = 0.02;
const DIVIDER_HEATER_GAP = 0.02;

/**
 * Wall margin used by `calculateHeaterPlacement`'s two-room default centre
 * (`WALL_MARGIN_MM = 150`).
 */
const HEATER_WALL_MARGIN_M = 0.15;

/**
 * The hot room's upper bench front, measured from the finished face of the end
 * wall it hangs on (centre 0.4 m in, 0.6 m deep) — the constraint the split
 * bound below has always expressed, and the limit a divider-mounted stove may
 * slide toward.
 */
const UPPER_BENCH_FRONT_FROM_END_WALL_M = 0.4 + 0.3;

/**
 * The LOWER bench reaches this far off the same end wall (see the NOTE in
 * `getPartitionSplitBounds`). The split bound does not guard it, but a stove
 * sliding off the divider must never crowd it.
 */
const LOWER_BENCH_FRONT_FROM_END_WALL_M = 1.1;

/**
 * How far past its minimum gap a divider-mounted wood stove may stand off the
 * divider shield so that its flue lands in a roof-joist bay instead of cutting
 * a joist. More clearance behind a stove is never less safe; the cap keeps it
 * reading as wall-backed.
 */
export const MAX_DIVIDER_HEATER_EXTRA_GAP_M = 0.3;

export interface DividerHeaterInput {
  /** Building length — the roof joists are laid out along it. */
  lengthM: number;
  /** Divider centre line. */
  dividerZM: number;
  /**
   * The engines' `roomLayout.saunaDirection`: the stove stands at
   * `dividerZ − saunaDirection · offset`, and the hot room's far end wall is
   * on that same side.
   */
  saunaDirection: 1 | -1;
  /** Finished face of the hot room's far end wall, where the benches hang. */
  saunaEndZM: number;
  heater: HeaterRef;
}

/**
 * Centre of a divider-mounted wood stove (two-room with a relax room): behind
 * its shield at the minimum gap, then slid away from the divider — up to
 * `MAX_DIVIDER_HEATER_EXTRA_GAP_M`, never closer than `HEATER_BENCH_CLEARANCE_M`
 * to the lower bench — to the nearest spot where the flue clears the joists.
 * Where no such spot exists it stays at the minimum gap; the flue then needs a
 * trimmed opening (växling) in the joists.
 */
export function dividerHeaterCenterZM(input: DividerHeaterInput): number {
  const heaterD = getHeaterDepthM(input.heater);
  const away = -input.saunaDirection as 1 | -1;
  const nearest =
    input.dividerZM +
    away * (DIVIDER_FACE_OFFSET + DIVIDER_SHIELD_T + DIVIDER_HEATER_GAP + heaterD / 2);
  const benchFrontZ =
    input.saunaEndZM + input.saunaDirection * LOWER_BENCH_FRONT_FROM_END_WALL_M;
  // Room left between the stove's far edge and the bench (clearance kept).
  const roomToBench =
    away * (benchFrontZ - (nearest + (away * heaterD) / 2)) - HEATER_BENCH_CLEARANCE_M;
  const maxShift = Math.min(MAX_DIVIDER_HEATER_EXTRA_GAP_M, Math.max(0, roomToBench));
  const shift = chimneyBayShiftM(nearest, away, input.lengthM, maxShift) ?? 0;
  return nearest + away * shift;
}

/**
 * Distance from the divider centre line to the heater's BACK edge (the face
 * looking at the benches), derived from where the two-room engine actually
 * puts the heater.
 *
 * A partition only exists when there is a relax room, and in that layout
 * `buildTwoRoomInterior` splits by heater type:
 *
 *   - **wood** → mounted against the divider behind its heat shield, so the
 *     centre sits `dividerFaceOffset + shield + gap + heaterD/2` off the
 *     divider and the back edge another `heaterD/2` beyond.
 *   - **electric** → freestanding at `calculateHeaterPlacement`'s two-room
 *     default centre, `SD + IL + heaterW/2 + WALL_MARGIN` off the divider the
 *     function was given — plus one extra `WALL_BUILDOUT`, because the engine
 *     passes `roomLayout.saunaLength / L` (an INNER-length fraction) where
 *     `calculateHeaterPlacement` expects a divider-position fraction. That
 *     mismatch is the engine's, not this module's: the bound has to describe
 *     the heater that is actually drawn, so it is mirrored here and pinned by
 *     `partitionSplit.test.ts` against the built scene. Fixing the fraction in
 *     twoRoomSauna.ts will fail that test, which is the intent.
 *
 * The previous formula (`SD + heaterW/2 + 0.15 + heaterD/2`) applied the
 * freestanding, X-axis wall-margin layout to every heater type and dropped the
 * `iL` term — 0.665 m for a wood heater against an actual 0.572 m (issue #49
 * item 2), which made the UI slider needlessly strict.
 */
export function getHeaterFootprintFromDivider(heater: HeaterRef): number {
  const heaterD = getHeaterDepthM(heater);
  if (heaterKindOf(heater) === 'wood') {
    return DIVIDER_FACE_OFFSET + DIVIDER_SHIELD_T + DIVIDER_HEATER_GAP + heaterD;
  }
  return (
    WALL_BUILDOUT +
    SD +
    IL +
    getHeaterWidthM(heater) / 2 +
    HEATER_WALL_MARGIN_M +
    heaterD / 2
  );
}

function roundToStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

export interface PartitionBoundsOptions {
  /** Heater type — when provided, the min bound accounts for heater clearance. */
  heaterType?: HeaterType;
  /** Chosen catalog model — its box replaces the type default (issue 159). */
  heaterModelId?: string | null;
}

export function getPartitionSplitBounds(lengthM: number, options?: PartitionBoundsOptions) {
  let min = Math.max(0, PARTITION_SAUNA_MIN_LENGTH_M / Math.max(lengthM, 0.1));
  const max = Math.min(1, 1 - (PARTITION_RELAX_MIN_LENGTH_M / Math.max(lengthM, 0.1)));

  if (options?.heaterType) {
    // The heater is placed relative to the divider wall; its back edge extends
    // into the sauna room toward the benches at the far (back) wall.
    //
    // Heater back edge:  dividerZ - getHeaterFootprintFromDivider(heaterType)
    // Upper bench front edge (fixed off the sauna's back wall — the engine puts
    // the upper bench centre 0.4 m into the room with a 0.6 m depth, measured
    // from the finished inner face at -L/2 + SD + IL + PT):
    //   benchFrontZ = -L/2 + WALL_BUILDOUT + 0.4 + 0.3
    // Constraint: heater back edge > bench front edge + clearance
    //   -L/2 + L*split - footprint > -L/2 + benchFront + clearance
    //   L*split > footprint + benchFront + clearance
    //
    // NOTE: the engine also draws a LOWER bench reaching 1.1 m off that same
    // inner face. This bound only guards the upper bench, which is the
    // constraint it has always expressed — widening it to the lower bench is a
    // separate change (it would push the minimum sauna room past 1.9 m).
    const heaterFootprintFromDivider = getHeaterFootprintFromDivider({
      heaterType: options.heaterType,
      heaterModelId: options.heaterModelId,
    });
    const benchFrontFromBackWall = WALL_BUILDOUT + UPPER_BENCH_FRONT_FROM_END_WALL_M;
    const minSplitForHeater = (heaterFootprintFromDivider + benchFrontFromBackWall + HEATER_BENCH_CLEARANCE_M) / Math.max(lengthM, 0.1);
    min = Math.max(min, minSplitForHeater);
  }

  return {
    min: Number(min.toFixed(4)),
    max: Number(max.toFixed(4)),
  };
}

export function normalizePartitionSplit(lengthM: number, split: number, options?: PartitionBoundsOptions) {
  const { min, max } = getPartitionSplitBounds(lengthM, options);
  const snappedLength = roundToStep(split * lengthM, PARTITION_SPLIT_STEP_M);
  const snappedSplit = snappedLength / Math.max(lengthM, 0.1);
  const clamped = Math.min(max, Math.max(min, snappedSplit));

  return Number(clamped.toFixed(4));
}

/**
 * Clamp a split to the buildable bounds (min sauna / relax lengths) WITHOUT the
 * 0.1 m "nice dimensions" snap. This is the canonical split for geometry and the
 * partition-framing blocking calc, so the rendered divider and the BOM agree.
 * The 0.1 m snap stays a UI-input concern (see {@link normalizePartitionSplit}).
 *
 * Note: callers normally omit `options`, so the heater-footprint lower bound is
 * NOT applied here — heater↔bench clearance is enforced separately at placement
 * time. Pass `options.heaterType` only if you specifically need that tighter min.
 */
export function clampPartitionSplit(lengthM: number, split: number, options?: PartitionBoundsOptions) {
  const { min, max } = getPartitionSplitBounds(lengthM, options);
  return Number(Math.min(max, Math.max(min, split)).toFixed(4));
}

export function isPartitionSplitValid(lengthM: number, split: number, options?: PartitionBoundsOptions) {
  const { min, max } = getPartitionSplitBounds(lengthM, options);
  return split >= min && split <= max;
}

